"use client";

import Script from "next/script";
import { useEffect, useState, type JSX } from "react";
import {
  getConsentState,
  hydrateConsentState,
  readLocalStorage,
  subscribeToConsentState,
  type ConsentState,
} from "@/lib/consent";

const GA_MEASUREMENT_ID = "G-NCQXYZPPQD";
const CONTENTSQUARE_SRC = "https://t.contentsquare.net/uxa/ceca5afe39b03.js";

/**
 * Google Analytics + Contentsquare, moved out of the (Server Component) root
 * layout because whether they may load is only knowable client-side. Both
 * the server-rendered HTML and the first client render are "pending"
 * (renders null) — no hydration mismatch; the scripts only get appended
 * after mount, once consent resolves to "accepted".
 */
export function ConsentedScripts(): JSX.Element | null {
  const [state, setState] = useState<ConsentState>(getConsentState());

  useEffect(() => {
    hydrateConsentState(readLocalStorage());
    return subscribeToConsentState(setState);
  }, []);

  if (state !== "accepted") return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}');
        `}
      </Script>
      {process.env.NODE_ENV === "production" && (
        <Script src={CONTENTSQUARE_SRC} strategy="afterInteractive" />
      )}
    </>
  );
}
